/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';

import StretchEnum from '../stretch/stretch-enum';

/**
 * Ovladač pro zobrazování obrázkových souborů.
 * Namísto standardního zobrazování v `<img>`, provádí ovladač zobrazení
 * obrázků jako CSS background-image na obyčejném `<div>`.
 * Tento přístup byl zvolen, protože má lepší výkon.
 * U načítání `<img>` na starších zařízeních dochází k chvilkovému zaseknuní
 * vykreslování prohlížeče, což vadí u výkonu zobrazovaných médií.
 * Problém je viditelný především na animacích typu marqee.
 * Kvůli omezením CSS neumí ovladač detekovat dokončení načtení obrázku,
 * událost se vyvolá ihned po začátku načítání.
 * Také se neprovádí žádná detekce selhání při načítání obrázku.
 * @implements {ViewerController}
 */
class ImageViewerController {
  /**
   * Vytvoří instanci.
   * @param {ViewerLifeCycleFactory} lifeCycleFactory
   */
  constructor(lifeCycleFactory) {
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.scheduleInfo = null;
    this.viewerLifeCycle = lifeCycleFactory.create();
    this.viewerSource = null;
  }

  /** @inheritdoc */
  destroy() {
    // Prázdná metoda pro splnění rozhraní.
  }

  /** @inheritdoc */
  enter() {
    return this.viewerLifeCycle.enter(() => {
      // Zobrazení obalujícího elementu.
      const viewerFrame = this.viewerSource.getFrame();
      viewerFrame.enter();
    });
  }

  /**
   * Určí CSS třídu s roztažením, která se má přiřadit elementu.
   * @param {StretchEnum} stretchEnum
   * @private
   * @returns {string} Název CSS třídy.
   */
  getStretchClassName(stretchEnum) {
    switch (stretchEnum) {
      case StretchEnum.CONTAIN:
        return 'image-viewer-controller--stretch-contain';
      case StretchEnum.COVER:
        return 'image-viewer-controller--stretch-cover';
      case StretchEnum.FILL:
        return 'image-viewer-controller--stretch-fill';
      default:
        throw new Error(`Invalid stretch "${stretchEnum}".`);
    }
  }

  /** @inheritdoc */
  leave() {
    return this.viewerLifeCycle.leave(() => {
      // Skrytí obalujícího elementu.
      const viewerFrame = this.viewerSource.getFrame();
      viewerFrame.leave();
    });
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /** @inheritdoc */
  release() {
    return this.viewerLifeCycle.release(() => {
      const { viewerSource } = this;
      // Uvolnění obalujícího elementu.
      const viewerFrame = viewerSource.getFrame();
      viewerFrame.release();
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
      this.viewerSource = null;
    });
  }

  /** @inheritdoc */
  seize(scheduleInfo, viewerSource) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      this.viewerSource = viewerSource;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const stretchEnum = scheduleInfo.getStretchEnum();
        const el = viewerSource.getEl();
        const viewerFrame = viewerSource.getFrame();
        const stretchClassName = this.getStretchClassName(stretchEnum);
        // Příprava obalujícího elementu.
        viewerFrame.seize(scheduleInfo);
        // Příprava zdrojového elementu.
        el.classList.add('image-viewer-controller', stretchClassName);
        // Nastavení zdroje.
        el.style.backgroundImage = `url("${url}")`;
        // U CSS pozadí nelze sledovat okamžik dokončení.
        // Událost se tedy vyvolá alespoň ihned,
        // i když možná není načtení provedeno.
        this.emitter.emit('loaded');
      });
    });
  }
}

export default ImageViewerController;
